import { FC, MouseEventHandler, ReactNode } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { SharedContactDealerWrapper } from '../../contactDealer/SharedContactDealerWrapper';
import { startConversationMessage } from '../pdpMessages';

// helpers
import { PRICE_TYPES } from 'dibs-buyer-price/src/constants';
import { includes } from 'dibs-ts-utils/exports/includes';

// styles
import styles from './PdpSharedButtons.scss';

import { PdpSharedContactDealerButton_item$data } from './__generated__/PdpSharedContactDealerButton_item.graphql';
import { PdpSharedContactDealerButton_user$data } from './__generated__/PdpSharedContactDealerButton_user.graphql';

type Props = {
    item: PdpSharedContactDealerButton_item$data;
    user: PdpSharedContactDealerButton_user$data | null;
    size?: 'small' | 'medium' | 'large';
    selectedSkuId?: string;
    source?: string;
    type?:
        | 'CONTACT_DEALER'
        | 'REQUEST_CUSTOMIZATION'
        | 'REQUEST_PRICE'
        | 'REQUEST_IMAGES'
        | 'REQUEST_IMAGES_AND_VIDEOS'
        | 'DESIGN_YOUR_OWN'
        | 'REQUEST_CUSTOMIZATION_ALT';
    fullWidth?: boolean;
    launchOrigin?: string;
    buttonType?: 'primary' | 'secondary' | 'textCta' | 'link' | 'transparent';
    wrapperClassName?: string;
    displayInline?: boolean;
};

const cannotCustomize = (item: PdpSharedContactDealerButton_item$data): boolean =>
    includes([PRICE_TYPES.UNAVAILABLE, PRICE_TYPES.SOLD], item?.priceType?.[0]?.textType);

export const PdpSharedContactDealerButtonComponent: FC<Props> = ({
    fullWidth,
    type,
    launchOrigin,
    item,
    selectedSkuId,
    size,
    buttonType,
    source,
    user = null,
    wrapperClassName,
    displayInline,
}) => {
    const contactTheSellerMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.contactTheSeller"
            defaultMessage="Contact Seller"
        />
    );

    const requestCustomizationMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestCustomization"
            defaultMessage="Request Customization"
        />
    );

    const requestPriceMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestPrice"
            defaultMessage="Request Price"
        />
    );

    const requestMoreImages = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestMoreImages"
            defaultMessage="Request More Images"
        />
    );
    const requestMoreImagesAndVideos = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestMore"
            defaultMessage="Request More"
        />
    );
    const designYourOwn = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.designYourOwn"
            defaultMessage="Design Your Own"
        />
    );

    let modalType = 'CONTACT_DEALER';
    let message: ReactNode;

    switch (type) {
        case 'REQUEST_PRICE':
            message = requestPriceMessage;
            launchOrigin = 'request price button';
            modalType = type;
            break;
        case 'REQUEST_CUSTOMIZATION':
            message = requestCustomizationMessage;
            launchOrigin = 'contact link in pdp - request customization';
            modalType = type;
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        case 'REQUEST_IMAGES':
            message = requestMoreImages;
            modalType = type;
            break;
        case 'REQUEST_IMAGES_AND_VIDEOS':
            message = requestMoreImagesAndVideos;
            modalType = type;
            break;
        case 'DESIGN_YOUR_OWN':
            message = designYourOwn;
            modalType = type;
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        case 'REQUEST_CUSTOMIZATION_ALT':
            message = requestCustomizationMessage;
            modalType = 'DESIGN_YOUR_OWN';
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        default:
            message =
                source === 'shipment-info-link'
                    ? contactTheSellerMessage
                    : startConversationMessage;
            break;
    }

    return (
        <div
            className={classnames(styles.buttonWrapper, wrapperClassName, {
                [styles.displayInline]: displayInline,
            })}
        >
            <CurrencyProvider
                render={({ currency }: { currency?: string } = {}) => (
                    <SharedContactDealerWrapper
                        item={item}
                        selectedSkuId={selectedSkuId}
                        launchOrigin={launchOrigin}
                        modalType={modalType}
                        user={user}
                        currency={currency}
                        displayInline={displayInline}
                    >
                        {(onClick: MouseEventHandler) =>
                            source === 'shipment-info-link' || buttonType === 'link' ? (
                                <Link
                                    onClick={onClick}
                                    className={classnames(
                                        styles.contactSellerLink,
                                        {
                                            [styles.linkButton]: buttonType === 'link',
                                        },
                                        styles.printHide
                                    )}
                                    dataTn="item-actions-request-information"
                                >
                                    {message}
                                </Link>
                            ) : (
                                <Button
                                    onClick={onClick}
                                    type={buttonType}
                                    className={classnames(
                                        { [styles.fullWidth]: fullWidth },
                                        styles.printHide
                                    )}
                                    dataTn="item-actions-request-information"
                                    size={size}
                                >
                                    {message}
                                </Button>
                            )
                        }
                    </SharedContactDealerWrapper>
                )}
            />
        </div>
    );
};

PdpSharedContactDealerButtonComponent.defaultProps = {
    size: 'large',
    buttonType: 'primary',
    type: 'CONTACT_DEALER',
    launchOrigin: 'contact link in pdp - sidebar',
    fullWidth: true,
};

export const PdpSharedContactDealerButton = createFragmentContainer(
    PdpSharedContactDealerButtonComponent,
    {
        item: graphql`
            fragment PdpSharedContactDealerButton_item on Item
            @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
                ...SharedContactDealerWrapper_item
                priceType: displayPrice(isTrade: false, buyerId: $userId) {
                    textType
                }
            }
        `,
        user: graphql`
            fragment PdpSharedContactDealerButton_user on User {
                ...SharedContactDealerWrapper_user
            }
        `,
    }
);
